/**
 * One lane of the board's loading skeleton (Figma 80:16484).
 *
 * A ghost of `KanbanColumnView`: same width, same 12px radius, same lane fill,
 * so when the real board lands nothing shifts sideways. Only the CONTENTS are
 * placeholders — a title bar and N card blocks, each with an avatar disc and
 * three text bars where the name, meta line and footer will be.
 *
 * ## Every placeholder is the `Skeleton` atom
 *
 * Not hand-built Views. The atom already carries the pulse animation and owns
 * its own radius through `variant` (`circular` / `rounded`), which is why the
 * avatar disc is a variant rather than a pill-radius class written out at this
 * call site — the design-system budget counts that class in feature views
 * precisely to push this kind of shape back into the library. The node
 * specifies no animation of its own; the atom's pulse is what this library does
 * for loading, and it reads better than flat blocks.
 *
 * ## Colours are the same surface tokens the real board uses
 *
 * page `surface-raised` < lane `surface-subtle` < card `surface-base`, which is
 * exactly the node's #f6f6f6 / #f2f1f1 / #ffffff. Because they are tokens they
 * also invert in dark, so the skeleton keeps a depth order there instead of
 * going flat.
 * @remarks View component — no business logic, hooks, or state.
 * @module features/player-kanban/components/organisms/KanbanColumn
 */

import React, { memo } from "react";

import { Box } from "@/components/atoms/Box";
import { Skeleton } from "@/components/atoms/Skeleton";
import { Stack } from "@/components/atoms/Stack";

/**
 * Placeholder fill for bars sitting ON a card (white) — one step darker than
 * the card, matching the node's #f2f1f1.
 */
const BAR_ON_CARD = "bg-surface-subtle";

/** Props for {@link KanbanColumnSkeletonView}. */
export interface KanbanColumnSkeletonViewProps {
  /**
   * How many card placeholders this lane shows. The board varies it per lane
   * so the skeleton reads as a board mid-load rather than a repeating pattern.
   */
  readonly cardCount: number;
}

/**
 * One placeholder card. Laid out with flex rather than the node's absolute
 * offsets — same result, and it cannot drift out of alignment if a size changes.
 * @param key - Index within the lane; these blocks have no identity of their own.
 * @returns The card block.
 */
const renderCard = (key: number) => (
  <Box
    key={key}
    className="h-24 w-full overflow-hidden rounded-md bg-surface-base px-2 pt-3"
  >
    <Stack direction="horizontal" className="items-start gap-2">
      <Skeleton
        variant="circular"
        startColor={BAR_ON_CARD}
        className="size-8"
      />
      <Stack className="min-w-0 flex-1 gap-2 pt-1">
        <Skeleton
          variant="rounded"
          startColor={BAR_ON_CARD}
          className="h-2 w-24"
        />
        <Skeleton
          variant="rounded"
          startColor={BAR_ON_CARD}
          className="h-2 w-20"
        />
      </Stack>
    </Stack>
    <Box className="pt-4">
      <Skeleton
        variant="rounded"
        startColor={BAR_ON_CARD}
        className="h-2 w-40"
      />
    </Box>
  </Box>
);

/**
 * Render one skeleton lane.
 * @param root0 - Component props.
 * @param root0.cardCount - How many card placeholders to show.
 */
const KanbanColumnSkeletonView = ({
  cardCount,
}: KanbanColumnSkeletonViewProps) => (
  <Box
    aria-hidden
    className="w-72 min-w-72 shrink-0 gap-2 rounded-lg bg-surface-subtle px-2 py-3"
  >
    {/* The lane's title bar is WHITE on the grey lane — the inverse of the bars
        inside the cards, exactly as the node draws it. */}
    <Skeleton
      variant="rounded"
      startColor="bg-surface-base"
      className="h-3 w-28"
    />
    <Stack className="gap-2">
      {Array.from({ length: cardCount }, (_, index) => renderCard(index))}
    </Stack>
  </Box>
);

KanbanColumnSkeletonView.displayName = "KanbanColumnSkeletonView";

export default memo(KanbanColumnSkeletonView);
